import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes, faSort, faSearch, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/theme';
import { Grid, Button, Container, Section } from '../styles/components';
import ProductCard from '../components/product/ProductCard';
import { mockProducts, mockCategories } from '../utils/mockData';
import { Product } from '../utils/types';
import PageHero from '../components/layout/PageHero';
import { Link } from 'react-router-dom';

const PageContainer = styled.div`
  padding-bottom: ${theme.spacing.xl};
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${theme.spacing.md};
    align-items: stretch;
  }
`;

const FilterControls = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    justify-content: space-between;
  }
`;

const FilterButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const SortSelect = styled.select`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.small};
  border: 1px solid ${theme.colors.gray};
  background-color: ${theme.colors.background};
  font-family: inherit;
`;

const FilterSidebar = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 300px;
  height: 100vh;
  background-color: ${theme.colors.background};
  padding: ${theme.spacing.lg};
  transform: translateX(${({ isOpen }) => (isOpen ? '0' : '-100%')});
  transition: transform 0.3s ease-in-out;
  box-shadow: ${theme.shadows.medium};
  z-index: 1000;
  overflow-y: auto;
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 1px solid ${theme.colors.lightGray};
`;

const FilterTitle = styled.h3`
  font-size: 1.25rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: ${theme.colors.darkGray};
  
  &:hover {
    color: ${theme.colors.secondary};
  }
`;

const FilterSection = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const FilterSectionTitle = styled.h4`
  margin-bottom: ${theme.spacing.sm};
  font-size: 1rem;
`;

const FilterOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  
  input {
    margin-right: ${theme.spacing.sm};
  }
`;

const ApplyButton = styled(Button)`
  width: 100%;
  margin-top: ${theme.spacing.md};
`;

const Backdrop = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
  transition: opacity 0.3s ease-in-out;
  z-index: 999;
`;

const NoResults = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl} 0;
  
  h3 {
    margin-bottom: ${theme.spacing.md};
  }
  
  p {
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.darkGray};
  }
`;

const ActiveFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const FilterTag = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background-color: ${theme.colors.lightGray};
  border-radius: ${theme.borderRadius.small};
  font-size: 0.875rem;
  
  button {
    background: none;
    border: none;
    cursor: pointer;
    color: ${theme.colors.darkGray};
    display: flex;
    align-items: center;
    
    &:hover {
      color: ${theme.colors.secondary};
    }
  }
`;

const ResultCount = styled.div`
  color: ${theme.colors.darkGray};
  margin-bottom: ${theme.spacing.md};
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: ${theme.colors.backgroundAlt};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.small};
  
  input {
    flex: 1;
    border: none;
    background: transparent;
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-family: inherit;
    font-size: 1rem;
    
    &:focus {
      outline: none;
    }
  }
  
  button {
    background: none;
    border: none;
    color: ${theme.colors.textLight};
    cursor: pointer;
    
    &:hover {
      color: ${theme.colors.primary};
    }
  }
`;

const CategoryShowcase = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const CategoryTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: ${theme.spacing.md};
  font-family: ${theme.fonts.heading};
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: ${theme.colors.gradient.gold};
    border-radius: ${theme.borderRadius.pill};
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const CategoryCard = styled.div<{ active: boolean }>`
  border-radius: ${theme.borderRadius.medium};
  overflow: hidden;
  position: relative;
  aspect-ratio: 1;
  cursor: pointer;
  box-shadow: ${props => props.active ? theme.shadows.medium : theme.shadows.small};
  transform: ${props => props.active ? 'scale(1.05)' : 'scale(1)'};
  transition: all 0.3s ease;
  border: ${props => props.active ? `2px solid ${theme.colors.primary}` : 'none'};
  
  &:hover {
    transform: scale(1.05);
    box-shadow: ${theme.shadows.medium};
  }
`;

const CategoryImage = styled.div<{ image: string }>`
  width: 100%;
  height: 100%;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.2) 0%,
      rgba(0, 0, 0, 0.7) 100%
    );
  }
`;

const CategoryName = styled.span`
  position: absolute;
  bottom: ${theme.spacing.sm};
  left: ${theme.spacing.sm};
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
`;

const FeaturedSection = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
`;

const ViewAllLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  color: ${theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

// Define the allowed categories
const ALLOWED_CATEGORIES = ['perfumes', 'gift-sets'];

// Filter the product categories to only show Perfumes and Gift Sets
const filteredCategories = mockCategories.filter(category => 
  ALLOWED_CATEGORIES.includes(category.id)
);

const ProductsPage: React.FC = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Filter products to only include Perfumes and Gift Sets, and limit to 4 products
  const [products, setProducts] = useState<Product[]>(
    mockProducts
      .filter(product => ALLOWED_CATEGORIES.includes(product.category))
      .slice(0, 4)
  );
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<{
    categories: string[];
    minPrice: number | null;
    maxPrice: number | null;
  }>({
    categories: [],
    minPrice: null,
    maxPrice: null,
  });
  const [sortBy, setSortBy] = useState('default');
  
  // Apply filters and sorting
  useEffect(() => {
    let result = [...products];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(product => 
        product.name.toLowerCase().includes(term) || 
        product.description.toLowerCase().includes(term) ||
        product.category.toLowerCase().includes(term)
      );
    }
    
    // Apply category filter
    if (activeFilters.categories.length > 0) {
      result = result.filter(product => 
        activeFilters.categories.includes(product.category)
      );
    }
    
    // Apply price filter
    if (activeFilters.minPrice !== null) {
      result = result.filter(product => {
        const price = product.discountPrice || product.price;
        return price >= activeFilters.minPrice!;
      });
    }
    
    if (activeFilters.maxPrice !== null) {
      result = result.filter(product => {
        const price = product.discountPrice || product.price;
        return price <= activeFilters.maxPrice!;
      });
    }
    
    // Apply sorting
    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => {
          const priceA = a.discountPrice || a.price;
          const priceB = b.discountPrice || b.price;
          return priceA - priceB;
        });
        break;
      case 'price-desc':
        result.sort((a, b) => {
          const priceA = a.discountPrice || a.price;
          const priceB = b.discountPrice || b.price;
          return priceB - priceA;
        });
        break;
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'rating-desc':
        result.sort((a, b) => {
          const ratingA = a.rating || 0;
          const ratingB = b.rating || 0;
          return ratingB - ratingA;
        });
        break;
      default:
        // Default sorting (newest/featured)
        break;
    }
    
    setFilteredProducts(result);
  }, [products, activeFilters, sortBy, searchTerm]);
  
  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };
  
  const handleCategoryChange = (categoryId: string) => {
    setActiveFilters(prevFilters => {
      const isSelected = prevFilters.categories.includes(categoryId);
      let newCategories;
      
      if (isSelected) {
        newCategories = prevFilters.categories.filter(id => id !== categoryId);
      } else {
        newCategories = [...prevFilters.categories, categoryId];
      }
      
      return {
        ...prevFilters,
        categories: newCategories,
      };
    });
  };
  
  const handleCategoryClick = (categoryId: string) => {
    setActiveFilters(prevFilters => {
      // If category is already selected, remove it
      if (prevFilters.categories.includes(categoryId)) {
        return {
          ...prevFilters,
          categories: prevFilters.categories.filter(id => id !== categoryId),
        };
      }
      // Otherwise add it
      return {
        ...prevFilters,
        categories: [...prevFilters.categories, categoryId],
      };
    });
  };
  
  const removeFilter = (type: string, value?: string) => {
    setActiveFilters(prevFilters => {
      if (type === 'category' && value) {
        return {
          ...prevFilters,
          categories: prevFilters.categories.filter(c => c !== value),
        };
      } else if (type === 'price-min') {
        return {
          ...prevFilters,
          minPrice: null,
        };
      } else if (type === 'price-max') {
        return {
          ...prevFilters,
          maxPrice: null,
        };
      } else if (type === 'all') {
        return {
          categories: [],
          minPrice: null,
          maxPrice: null,
        };
      }
      return prevFilters;
    });
  };
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search is already applied via useEffect
  };
  
  return (
    <PageContainer>
      <PageHero 
        title="Our Collection" 
        subtitle="Discover our exquisite range of luxury perfumes and fragrances"
        height="40vh"
      />
      
      <Section>
        <Container>
          {/* Category Showcase */}
          <CategoryShowcase>
            <CategoryTitle>Browse by Category</CategoryTitle>
            <CategoryGrid>
              {filteredCategories.map(category => (
                <CategoryCard 
                  key={category.id}
                  active={activeFilters.categories.includes(category.id)}
                  onClick={() => handleCategoryClick(category.id)}
                >
                  <CategoryImage image={category.image || ''} />
                  <CategoryName>{category.name}</CategoryName>
                </CategoryCard>
              ))}
            </CategoryGrid>
          </CategoryShowcase>
          
          <SearchContainer>
            <input 
              type="text" 
              placeholder="Search products..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button onClick={handleSearch}>
              <FontAwesomeIcon icon={faSearch} />
            </button>
          </SearchContainer>
        
          <FilterContainer>
            <FilterControls>
              <FilterButton onClick={toggleFilter} variant="outline">
                <FontAwesomeIcon icon={faFilter} /> Filter
              </FilterButton>
              {Object.values(activeFilters).some(
                val => (Array.isArray(val) && val.length > 0) || val !== null
              ) && (
                <FilterButton onClick={() => removeFilter('all')} variant="outline">
                  Clear All
                </FilterButton>
              )}
            </FilterControls>
            
            <div>
              <SortSelect 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="default">Sort: Featured</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
                <option value="name-desc">Name: Z to A</option>
                <option value="rating-desc">Highest Rated</option>
              </SortSelect>
            </div>
          </FilterContainer>
        
          {/* Active filters */}
          {(activeFilters.categories.length > 0 || 
            activeFilters.minPrice !== null || 
            activeFilters.maxPrice !== null) && (
            <ActiveFilters>
              {activeFilters.categories.map(category => {
                const categoryName = filteredCategories.find(c => c.id === category)?.name || category;
                return (
                  <FilterTag key={category}>
                    Category: {categoryName}
                    <button onClick={() => removeFilter('category', category)}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </FilterTag>
                );
              })}
              
              {activeFilters.minPrice !== null && (
                <FilterTag>
                  Min Price: ${activeFilters.minPrice}
                  <button onClick={() => removeFilter('price-min')}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </FilterTag>
              )}
              
              {activeFilters.maxPrice !== null && (
                <FilterTag>
                  Max Price: ${activeFilters.maxPrice}
                  <button onClick={() => removeFilter('price-max')}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </FilterTag>
              )}
            </ActiveFilters>
          )}
        
          <ResultCount>
            Showing {filteredProducts.length} products
          </ResultCount>
        
          <div id="products-grid">
            {filteredProducts.length > 0 ? (
              <Grid 
                columns={4} 
                tabletColumns={2} 
                mobileColumns={1}
                gap={theme.spacing.lg}
              >
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </Grid>
            ) : (
              <NoResults>
                <h3>No Products Found</h3>
                <p>Try adjusting your filters or search criteria.</p>
                <Button onClick={() => removeFilter('all')}>Clear All Filters</Button>
              </NoResults>
            )}
          </div>
        </Container>
      </Section>
      
      {/* Filter sidebar */}
      <FilterSidebar isOpen={isFilterOpen}>
        <FilterHeader>
          <FilterTitle>Filter Products</FilterTitle>
          <CloseButton onClick={toggleFilter}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </FilterHeader>
        
        <FilterSection>
          <FilterSectionTitle>Categories</FilterSectionTitle>
          <FilterOptions>
            {filteredCategories.map(category => (
              <FilterOption key={category.id}>
                <input 
                  type="checkbox" 
                  id={`category-${category.id}`} 
                  checked={activeFilters.categories.includes(category.id)}
                  onChange={() => handleCategoryChange(category.id)}
                />
                <span>{category.name}</span>
              </FilterOption>
            ))}
          </FilterOptions>
        </FilterSection>
        
        <FilterSection>
          <FilterSectionTitle>Price Range</FilterSectionTitle>
          <FilterOptions>
            {/* Price range options can be added here if needed */}
          </FilterOptions>
        </FilterSection>
        
        <ApplyButton onClick={toggleFilter} variant="primary">
          Apply Filters
        </ApplyButton>
      </FilterSidebar>
      
      <Backdrop isVisible={isFilterOpen} onClick={toggleFilter} />
    </PageContainer>
  );
};

export default ProductsPage; 